import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack, Typography, styled,
} from "@mui/material";
import { CustomButton } from "@/components/custom/custom-button";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { FormTextArea } from "@/components/custom/custom-form-textarea";
import { showToast } from "@/redux/actions/toast-actions";
import { messageTemplateSchema } from "@/schemas/notification.schema";
import { useCreateMessageTemplate, useUpdateMessageTemplate } from "@/hooks/use-notifications";
import { SMS_EVENT_STATUS_OPTIONS } from "../../data";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 520,
    width: "100%",
  },
}));

const SEGMENT_SIZE = 160;

const TemplateFormDialog = ({ open, template, onClose }) => {
  const dispatch = useDispatch();
  const isEdit = Boolean(template);
  const createMutation = useCreateMessageTemplate();
  const updateMutation = useUpdateMessageTemplate();
  const mutation = isEdit ? updateMutation : createMutation;

  const formik = useFormik({
    initialValues: {
      title: template?.title ?? "",
      smsEventStatus: template?.smsEventStatus ?? "",
      message: template?.message ?? "",
    },
    enableReinitialize: true,
    validationSchema: messageTemplateSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      const payload = {
        title: values.title.trim(),
        smsEventStatus: values.smsEventStatus,
        message: values.message.trim(),
      };

      const run = isEdit
        ? updateMutation.mutateAsync({ messageId: template.id, ...payload })
        : createMutation.mutateAsync(payload);

      run
        .then((res) => {
          dispatch(showToast(res?.message ?? (isEdit ? "Template updated" : "Template created"), "success"));
          onClose();
        })
        .catch((err) =>
          dispatch(showToast(err?.message ?? "Failed to save template", "error")),
        );
    },
  });

  useEffect(() => {
    if (open) mutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template]);

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    onClose();
  };

  const length = formik.values.message?.length ?? 0;
  const segments = length === 0 ? 0 : Math.ceil(length / SEGMENT_SIZE);

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
        <DialogTitle sx={{ p: 0, fontSize: "1.1rem", fontWeight: 600 }}>
          {isEdit ? "Edit Message Template" : "New Message Template"}
        </DialogTitle>

        {mutation.isError && (
          <CustomErrorMessage message={mutation.error?.message ?? "Failed to save template"} />
        )}

        <Stack spacing={2}>
          <FormTextInput
            formik={formik}
            name="title"
            label="Title"
            placeholder="e.g. Transaction successful"
            isDisabled={mutation.isPending}
          />
          <FormSelectInput
            formik={formik}
            name="smsEventStatus"
            label="Event"
            options={SMS_EVENT_STATUS_OPTIONS}
            isDisabled={mutation.isPending}
          />
          <Box>
            <FormTextArea
              formik={formik}
              name="message"
              label="Message"
              placeholder="The SMS body sent for this event..."
              rows={4}
              isDisabled={mutation.isPending}
            />
            {/* Billing is per 160-character segment, so the cost of a template
                is worth showing while it is being written. */}
            <Typography variant="caption" color="text.secondary">
              {length} characters • {segments} SMS segment{segments === 1 ? "" : "s"}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 1, pb: 1, gap: 1 }}>
        <CustomButton variantType="outline" size="small" onClick={handleClose} disabled={mutation.isPending} sx={{ px: 2 }}>
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending ? "Saving..." : isEdit ? "Save Changes" : "Create Template"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default TemplateFormDialog;